// vCard-Parsing für den Kontakt-Import vom Handy.
//
// iPhone und Android exportieren Kontakte als .vcf — das ist der einzige Weg, der auf
// *beiden* Plattformen ohne App funktioniert. Die Datei ist keine Zeile-pro-Kontakt-CSV,
// sondern hat eigene Regeln, die man leicht falsch macht:
//
//  - Zeilen dürfen umgebrochen werden ("folding"): eine Folgezeile beginnt mit Space/Tab
//    und gehört an die vorherige. Wer stur an \n splittet, zerreißt lange Felder.
//  - Werte sind escaped: \, \; \n haben Bedeutung.
//  - Werte können Base64/Quoted-Printable sein (vCard 2.1, kommt bei Android-Exporten vor).
//  - Ein Kontakt hat oft mehrere Telefonnummern/E-Mails — wir wollen die sinnvollste.

export interface VCardContact {
  name: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  role?: string;
  email?: string;
  phone?: string;
  website?: string;
  city?: string;
  country?: string;
  notes?: string;
  /** Foto als Data-URL, falls im vCard enthalten. */
  photoUrl?: string;
  /** Alle gefundenen Nummern/Adressen — die UI kann sie zur Auswahl anbieten. */
  allEmails: string[];
  allPhones: string[];
}

export interface VCardParseResult {
  contacts: VCardContact[];
  /** Nicht lesbare Einträge — sichtbar melden statt still verwerfen. */
  errors: string[];
}

/** Hebt die Zeilenumbrüche innerhalb eines Feldes wieder auf (RFC 6350 "line folding"). */
function unfold(text: string): string[] {
  const raw = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const out: string[] = [];
  for (const line of raw) {
    if ((line.startsWith(' ') || line.startsWith('\t')) && out.length > 0) {
      out[out.length - 1] += line.slice(1);
    } else {
      out.push(line);
    }
  }
  return out;
}

function unescapeValue(v: string): string {
  return v
    .replace(/\\n/gi, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\')
    .trim();
}

function decodeQuotedPrintable(v: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < v.length; i++) {
    if (v[i] === '=' && i + 2 < v.length) {
      const hex = v.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        bytes.push(parseInt(hex, 16));
        i += 2;
        continue;
      }
    }
    bytes.push(v.charCodeAt(i));
  }
  try {
    return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
  } catch {
    return v;
  }
}

interface VLine {
  name: string;
  params: Record<string, string>;
  value: string;
}

function parseLine(line: string): VLine | null {
  const colon = line.indexOf(':');
  if (colon < 0) return null;

  const head = line.slice(0, colon);
  let value = line.slice(colon + 1);

  const parts = head.split(';');
  const name = (parts[0] ?? '').toUpperCase();
  // Gruppen-Präfix ("item1.TEL") wegwerfen — Apple exportiert so.
  const bare = name.includes('.') ? name.slice(name.indexOf('.') + 1) : name;

  const params: Record<string, string> = {};
  const addType = (v: string) => {
    params['TYPE'] = `${params['TYPE'] ?? ''},${v}`.replace(/^,/, '').toUpperCase();
  };

  for (const p of parts.slice(1)) {
    const eq = p.indexOf('=');
    if (eq < 0) {
      // vCard 2.1 schreibt Parameter ohne Schlüssel: "TEL;CELL:..."
      addType(p);
      continue;
    }
    const key = p.slice(0, eq).toUpperCase();
    const val = p.slice(eq + 1);
    // TYPE darf mehrfach vorkommen ("TEL;type=CELL;type=VOICE;type=pref"). Überschreiben
    // würde CELL verlieren — genau so exportiert das iPhone.
    if (key === 'TYPE') addType(val);
    else params[key] = val;
  }

  if ((params['ENCODING'] ?? '').toUpperCase().includes('QUOTED-PRINTABLE')) {
    value = decodeQuotedPrintable(value);
  }

  return { name: bare, params, value };
}

/** Bevorzugt Mobilnummern — für ein Business-Netzwerk ist das die brauchbarste. */
function pickPhone(entries: { type: string; value: string }[]): string | undefined {
  const cell = entries.find((e) => /CELL|MOBILE/i.test(e.type));
  if (cell) return cell.value;
  const work = entries.find((e) => /WORK/i.test(e.type));
  if (work) return work.value;
  return entries[0]?.value;
}

function pickEmail(entries: { type: string; value: string }[]): string | undefined {
  const work = entries.find((e) => /WORK|PREF/i.test(e.type));
  if (work) return work.value;
  return entries[0]?.value;
}

export function parseVCards(text: string): VCardParseResult {
  const lines = unfold(text);
  const contacts: VCardContact[] = [];
  const errors: string[] = [];

  let cur: VLine[] | null = null;

  for (const line of lines) {
    const upper = line.trim().toUpperCase();
    if (upper === 'BEGIN:VCARD') {
      cur = [];
      continue;
    }
    if (upper === 'END:VCARD') {
      if (cur) {
        const c = buildContact(cur, errors);
        if (c) contacts.push(c);
      }
      cur = null;
      continue;
    }
    if (cur) {
      const parsed = parseLine(line);
      if (parsed) cur.push(parsed);
    }
  }

  if (contacts.length === 0 && errors.length === 0) {
    errors.push('Keine Kontakte gefunden. Ist das wirklich eine .vcf-Datei?');
  }

  return { contacts, errors };
}

function buildContact(lines: VLine[], errors: string[]): VCardContact | null {
  const get = (n: string) => lines.find((l) => l.name === n);

  const emails = lines
    .filter((l) => l.name === 'EMAIL' && l.value.trim())
    .map((l) => ({ type: l.params['TYPE'] ?? '', value: unescapeValue(l.value) }));
  const phones = lines
    .filter((l) => l.name === 'TEL' && l.value.trim())
    .map((l) => ({ type: l.params['TYPE'] ?? '', value: unescapeValue(l.value) }));

  // N ist strukturiert: Nachname;Vorname;Zusatz;Anrede;Suffix
  const n = get('N');
  let firstName: string | undefined;
  let lastName: string | undefined;
  if (n) {
    const parts = n.value.split(';').map((p) => unescapeValue(p));
    lastName = parts[0] || undefined;
    firstName = parts[1] || undefined;
  }

  const fn = get('FN');
  const name =
    (fn ? unescapeValue(fn.value) : '') ||
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    emails[0]?.value ||
    '';

  if (!name) {
    errors.push('Ein Eintrag ohne Namen wurde übersprungen.');
    return null;
  }

  const org = get('ORG');
  // ORG ist ebenfalls strukturiert (Firma;Abteilung) — nur die Firma interessiert uns.
  const company = org ? unescapeValue(org.value.split(';')[0] ?? '') || undefined : undefined;

  // ADR: PostfachExt;Ext;Straße;Ort;Region;PLZ;Land
  const adr = get('ADR');
  let city: string | undefined;
  let country: string | undefined;
  if (adr) {
    const p = adr.value.split(';').map((x) => unescapeValue(x));
    city = p[3] || undefined;
    country = p[6] || undefined;
  }

  const photo = get('PHOTO');
  let photoUrl: string | undefined;
  if (photo && photo.value.trim()) {
    const v = photo.value.trim();
    if (v.startsWith('data:')) {
      photoUrl = v;
    } else if ((photo.params['ENCODING'] ?? '').toUpperCase().match(/B|BASE64/)) {
      const type = (photo.params['TYPE'] ?? 'JPEG').toLowerCase();
      photoUrl = `data:image/${type === 'jpeg' ? 'jpeg' : type};base64,${v}`;
    }
  }

  const title = get('TITLE');
  const url = get('URL');
  const note = get('NOTE');

  return {
    name,
    firstName,
    lastName,
    company,
    role: title ? unescapeValue(title.value) || undefined : undefined,
    email: pickEmail(emails),
    phone: pickPhone(phones),
    website: url ? unescapeValue(url.value) || undefined : undefined,
    city,
    country,
    notes: note ? unescapeValue(note.value) || undefined : undefined,
    photoUrl,
    allEmails: emails.map((e) => e.value),
    allPhones: phones.map((p) => p.value),
  };
}

/**
 * Stabiler Fingerabdruck eines Kontakts — Grundlage dafür, dass ein weggeswipter Kontakt
 * beim nächsten Import nicht wieder auftaucht. Bewusst nur aus E-Mail/Telefon/Name
 * gebildet und normalisiert; gespeichert wird nur dieser Wert, nie der Kontakt selbst.
 */
export function contactFingerprint(c: {
  name: string;
  email?: string | null;
  phone?: string | null;
}): string {
  const email = (c.email ?? '').trim().toLowerCase();
  if (email) return `e:${email}`;
  const phone = (c.phone ?? '').replace(/[^\d+]/g, '');
  if (phone) return `p:${phone}`;
  return `n:${c.name.trim().toLowerCase()}`;
}
